import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  OutlinedInput,
  FormHelperText
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import {
  DepartmentReqModel,
  DepartmentsModel
} from '@models/departments-and-units/departments.model.ts';
import useDepartmentStore from '@modules/departments-and-units/pages/departments/store/departments.store.ts';
import {
  departmentsErrorMessages,
  departmentTypeList
} from '@modules/departments-and-units/utils/departments.utils.ts';
import TimeRangePickerList from '@components/time-range-picker-list/time-range-picker-list.tsx';
import dayjs from 'dayjs';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import {
  trimFormValues,
  validateNotOnlySpaces
} from '@utils/trim-form-values-utils.ts';
import { TimeRange } from '@models/time-range-picker.model.ts';
import { convertToSelectedAssignedAdmin } from '@modules/departments-and-units/utils/departments-and-units.utils.ts';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';

const DepartmentsDialogContent: React.FC<{
  onSave: (data: DepartmentReqModel) => void;
  assignedAdminList: { id: number; title: string; value: string }[];
  updateDepartmentData: DepartmentsModel | null;
}> = ({ onSave, updateDepartmentData, assignedAdminList }) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { isLoading } = useDepartmentStore();
  const {
    register,
    formState: { errors, isValid },
    control,
    handleSubmit,
    reset
  } = useForm<any>({ mode: 'onTouched' });
  const [timeRanges, setTimeRanges] = useState<TimeRange[]>([]);

  const typeSelected = useMemo(() => {
    const selected = departmentTypeList.find(
      (item) => item.value === updateDepartmentData?.type
    );
    return selected ? [selected] : [];
  }, [updateDepartmentData]);

  // const assignedAdminSelected = useMemo(() => {
  //   return convertToSelectedAssignedAdmin(updateDepartmentData);
  // }, [updateDepartmentData]);

  useEffect(() => {
    if (updateDepartmentData) {
      reset({
        departmentName: updateDepartmentData?.name || '',
        description: updateDepartmentData.description || '',
        type: typeSelected
        // assignedAdmin: assignedAdminSelected
      });

      if (updateDepartmentData?.availableHours?.length) {
        const converted: TimeRange[] = updateDepartmentData.availableHours.map(
          (item: any) => ({
            start: dayjs(item.open, 'HH:mm'),
            end: dayjs(item.close, 'HH:mm')
          })
        );
        setTimeRanges(converted);
      }
    }
  }, [updateDepartmentData, reset]);

  const handleTimeChange = (ranges: TimeRange[]) => {
    setTimeRanges(ranges);
  };

  const onSubmit = (data: DepartmentReqModel) => {
    const trimmedData = trimFormValues(data);

    const assignedAdmin = Array.isArray(data.assignedAdmin)
      ? data.assignedAdmin[0]
      : data.assignedAdmin;

    const type = Array.isArray(data.type) ? data.type[0] : data.type;

    const availableHours = timeRanges
      .filter(
        (range): range is { start: dayjs.Dayjs; end: dayjs.Dayjs } =>
          range.start !== null && range.end !== null
      )
      .map((range) => ({
        open: range.start.format('HH:mm'),
        close: range.end.format('HH:mm')
      }));

    onSave({
      name: trimmedData.departmentName,
      description: trimmedData.description,
      type: type.value,
      // adminId: assignedAdmin.id,
      availableHours
    });
  };

  return (
    <div className="mt-4">
      <form className="flex flex-col gap-1">
        <FormControl error={!!errors.departmentName}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_department_name')}
            <InfoTooltip text="tooltip_info_name" />
          </FormLabel>
          <OutlinedInput
            type="text"
            {...register('departmentName', {
              required: true,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            placeholder={t('placeholder_department_name')}
          />

          <FormHelperText>
            {errors?.departmentName &&
              getErrorMessage({
                errors,
                fieldName: 'departmentName',
                errorMessages: departmentsErrorMessages
              })}
          </FormHelperText>
        </FormControl>

        {/* <AppAutocompleteV2
          name="assignedAdmin"
          control={control}
          selectedList={assignedAdminSelected}
          label={
            <span className="flex items-center">
              {t('label_assigned_admin')}
              <InfoTooltip text="" />
            </span>
          }
          options={assignedAdminList}
          withChip={false}
          validationProps={{
            errors,
            errorMessages: departmentsErrorMessages,
            getErrorMessage,
            validationRules: { required: true }
          }}
        /> */}

        <AppAutocompleteV2
          name="type"
          control={control}
          selectedList={typeSelected}
          label={
            <span className="flex items-center">
              <span className="reuqired-symbole">*</span>
              {t('label_type')}
              <InfoTooltip text="tooltip_info_type" />
            </span>
          }
          placeholder={t('placeholder_type')}
          options={departmentTypeList}
          validationProps={{
            errors,
            errorMessages: departmentsErrorMessages,
            getErrorMessage,
            validationRules: { required: true }
          }}
        />

        <div className="w-full max-w-[600px]">
          <TimeRangePickerList
            updateData={timeRanges}
            onChange={handleTimeChange}
            mode={'multiple'}
            required={true}
            helperText={t('validation_working_time_required')}
          />
        </div>

        <FormControl error={!!errors.description}>
          <FormLabel className="flex">
            {t('label_description')}
            <InfoTooltip text="tooltip_info_description" />
          </FormLabel>
          <OutlinedInput
            multiline
            rows={5}
            {...register('description', {})}
            placeholder={t('placeholder_description')}
          />
          <FormHelperText>
            {errors?.description &&
              getErrorMessage({
                errors,
                fieldName: 'description',
                errorMessages: departmentsErrorMessages
              })}
          </FormHelperText>
        </FormControl>
      </form>

      <div className="flex justify-end pt-6 pb-1 sticky bottom-0 bg-white">
        <Button
          variant="contained"
          loading={isLoading()}
          disabled={
            !isValid ||
            !timeRanges.some((r) => r.start !== null && r.end !== null)
          }
          onClick={handleSubmit(onSubmit)}
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
};

export default DepartmentsDialogContent;
